import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import IconButton from '@mui/material/IconButton'
import LaunchIcon from '@mui/icons-material/Launch'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import Typography from '@mui/material/Typography'
import ArticleIcon from '@mui/icons-material/Article'
import Box from '@mui/material/Box'
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'

import { PaperModalStyle } from '../styles'
import { BootstrapDialog, BootstrapDialogTitle } from '../shared/BootstrapDialog'
import ResourceDescription from '../shared/ResourceDescription'

import useFavourite from '../../hooks/useFavourite'
import { copyResourceLink } from './copyResourceLink'
import { openExternal } from '../../utils/safeUrl'

const PaperModal = (props) => {
    const user = useSelector(state => state.user)
    const dispatch = useDispatch()

    const [open, setOpen] = useState(props.initialOpen || false)
    const { favourited, toggleFavourite } = useFavourite(props, 'paper')

    const hasAbstract = Boolean((props.abstract || '').trim())

    const handleClickOpen = () => {
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
        if (props.onExternalClose) props.onExternalClose()
    }

    const openResource = () => {
        openExternal(props.link)
        return false;
    }

    const handleCopyLink = async () => {
        await copyResourceLink({ dispatch, resourcePath: 'papers', resourceId: props.id })
    }

    return (
        <div>
            {!props.initialOpen && <Button {...PaperModalStyle.viewButton.props} sx={PaperModalStyle.viewButton.style} onClick={()=>handleClickOpen()}>
                More Info
            </Button>}
            <BootstrapDialog
                onClose={()=>handleClose()}
                aria-labelledby="customized-dialog-title"
                open={open}
                fullWidth
                {...PaperModalStyle.modal}
            >
                <BootstrapDialogTitle id="customized-dialog-title" onClose={()=>handleClose()}>
                    <Box sx={PaperModalStyle.headingContainer}>
                        <ArticleIcon sx={PaperModalStyle.icon} />
                        {props.title}
                    </Box>
                </BootstrapDialogTitle>
                <DialogContent dividers>
                    <Box sx={PaperModalStyle.content}>
                        <Box sx={PaperModalStyle.dateContainer}>
                            <Box sx={PaperModalStyle.dates}>
                                <Typography {...PaperModalStyle.heading}>
                                    Posted:&nbsp;
                                </Typography>
                                <Typography {...PaperModalStyle.text}>
                                    {props.updated.split('T')[0]}
                                </Typography>
                            </Box>
                            <Box sx={PaperModalStyle.dates}>
                                <Typography {...PaperModalStyle.heading}>
                                    Published:&nbsp;
                                </Typography>
                                <Typography {...PaperModalStyle.text}>
                                    {props.pub_date}
                                </Typography>
                            </Box>
                        </Box>
                        <Box>
                            <Box sx={PaperModalStyle.ratingContainer}>
                                {user && !user.anon && <IconButton onClick={toggleFavourite}>
                                    {favourited? <BookmarkIcon sx={PaperModalStyle.favColour} /> : <BookmarkBorderIcon sx={PaperModalStyle.favColour} />}
                                </IconButton>}
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={PaperModalStyle.mobileContainer}>
                        <Box sx={PaperModalStyle.mobileRating}>
                            {user && !user.anon && <IconButton onClick={toggleFavourite}>
                                {favourited? <BookmarkIcon sx={PaperModalStyle.favColour} /> : <BookmarkBorderIcon sx={PaperModalStyle.favColour} />}
                            </IconButton>}
                        </Box>
                        <Box sx={PaperModalStyle.mobileDate}>
                            <Typography gutterBottom {...PaperModalStyle.heading}>
                                Posted:&nbsp;
                            </Typography>
                            <Typography gutterBottom {...PaperModalStyle.text}>
                                {props.updated.split('T')[0]}
                            </Typography>
                        </Box>
                        <Box sx={PaperModalStyle.mobileDate}>
                            <Typography gutterBottom {...PaperModalStyle.heading}>
                                Published:&nbsp;
                            </Typography>
                            <Typography gutterBottom {...PaperModalStyle.text}>
                                {props.pub_date}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={PaperModalStyle.additionalInfo}>
                        <Typography {...PaperModalStyle.text} gutterBottom>
                            <strong>DOI:</strong> {props.doi}
                        </Typography>
                    </Box>
                    <Box sx={PaperModalStyle.additionalInfo}>
                        <Typography {...PaperModalStyle.text} gutterBottom>
                            <strong>Authors:</strong> {props.authors}
                        </Typography>
                    </Box>
                    <Grid container sx={PaperModalStyle.paperSpacing}>
                        {props.journal && <Grid item xs={12} sm={6} sx={PaperModalStyle.additionalInfo}>
                            <Typography {...PaperModalStyle.text} gutterBottom>
                                <strong>Journal:</strong> {props.journal}
                            </Typography>
                        </Grid>}
                        {props.volume && <Grid item xs={12} sm={6} sx={PaperModalStyle.additionalInfo}>
                            <Typography {...PaperModalStyle.text} gutterBottom>
                                <strong>Volume:</strong> {props.volume}
                            </Typography>
                        </Grid>}
                    </Grid>
                    <Grid container sx={PaperModalStyle.paperSpacing}>
                        {props.pages && <Grid item xs={12} sm={6} sx={PaperModalStyle.additionalInfo}>
                            <Typography {...PaperModalStyle.text} gutterBottom>
                                <strong>Suggested Pages:</strong> {props.pages}
                            </Typography>
                        </Grid>}
                        {props.issue && <Grid item xs={12} sm={6} sx={PaperModalStyle.additionalInfo}>
                            <Typography {...PaperModalStyle.text} gutterBottom>
                                <strong>Issue:</strong> {props.issue}
                            </Typography>
                        </Grid>}
                    </Grid>
                    <ResourceDescription
                        description={props.description}
                        containerSx={PaperModalStyle.additionalInfo}
                        typographyProps={{ ...PaperModalStyle.text, gutterBottom: true }}
                    />
                    {hasAbstract && (
                        <Box component='section' aria-labelledby={`paper-${props.id}-abstract-heading`} sx={PaperModalStyle.additionalInfo}>
                            <Typography {...PaperModalStyle.heading} component='h3' id={`paper-${props.id}-abstract-heading`} gutterBottom>
                                Abstract
                            </Typography>
                            <ResourceDescription
                                description={props.abstract}
                                typographyProps={{ ...PaperModalStyle.text, gutterBottom: true }}
                            />
                        </Box>
                    )}
                    {((props.tags && props.tags.length > 0) || (props.content_tags && props.content_tags.length > 0) || (props.medical_specialty_tags && props.medical_specialty_tags.length > 0)) &&
                    <><Box sx={PaperModalStyle.additionalInfo}>
                        <Typography {...PaperModalStyle.heading} gutterBottom>
                            Key Words
                        </Typography>
                    </Box>
                    <Box>
                        {props.tags && props.tags.map(tag => <Chip key={tag} label={tag} sx={PaperModalStyle.tags}/>)}
                        {props.content_tags && props.content_tags.map(tag => <Chip key={tag} label={tag} sx={PaperModalStyle.tags}/>)}
                        {props.medical_specialty_tags && props.medical_specialty_tags.map(tag => <Chip key={tag} label={tag} sx={PaperModalStyle.tags}/>)}
                    </Box></>}

                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCopyLink}>
                        Copy Link&nbsp;<ContentCopyIcon />
                    </Button>
                    <Button onClick={() => openResource()}>
                        View Resource&nbsp;<LaunchIcon />
                    </Button>
                    <Button autoFocus onClick={()=>handleClose()}>
                        Close
                    </Button>
                </DialogActions>
            </BootstrapDialog>
        </div>
    )
}

export default PaperModal
